/**
 * GCS upload — presign against the backend, PUT the bytes straight to the signed
 * URL, and return a usable https URL. Mirrors the customer app's proof/verification
 * upload flow so admins upload files instead of pasting URLs.
 */
import { api } from "./client";

export type UploadContext = "admin-proof" | "market-logo" | "chat-attachment" | "verification-doc" | "user-proof";

export interface PresignResponse {
  upload_url: string;
  public_url: string | null;
  object_key: string;
  expires_in_seconds: number;
}

export const presignUpload = (context: UploadContext, contentType: string) =>
  api.post<PresignResponse>("/admin/uploads/presign", { context, content_type: contentType });

export const readUrl = (objectKey: string) =>
  api.post<{ url: string }>("/admin/uploads/read-url", { object_key: objectKey });

/**
 * Presign → PUT bytes to GCS → return the object's PERMANENT public URL.
 *
 * Every caller stores the result (a payout proof, a market logo, a chat
 * attachment). A signed read URL expires within minutes, so it must never stand
 * in for the permanent one: a presign without `public_url` is an upload failure,
 * raised before any bytes are sent. (`readUrl` is for VIEWING a private object.)
 */
export async function uploadFile(file: File, context: UploadContext): Promise<string> {
  const contentType = file.type || "application/octet-stream";
  const p = await presignUpload(context, contentType);
  if (!p.public_url) {
    throw new Error(
      "Upload failed: the server did not return a permanent link for this file, so it was not uploaded.",
    );
  }
  const put = await fetch(p.upload_url, { method: "PUT", headers: { "Content-Type": contentType }, body: file });
  if (!put.ok) throw new Error(`Upload failed (${put.status})`);
  return p.public_url;
}
